import "./styles/material-dark.css";
import "./styles/editor.css";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-sql";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiThumbsDown, FiThumbsUp } from "react-icons/fi";

import { convertToTitleCase } from "../../actions/dataFormatter.js";
import { setMessageReaction, updateSql } from "../../api/messageApi.js";
import HectorIcon from "../../assets/icons/HectorIcon.jsx";
import Feedback from "../../pages/Feedback.js";
import { tableOptions } from "../../utils/Data.js";
import BarGraph from "../BarGraph/index.jsx";
import ChatTable from "../ChatTable/index.jsx";
import Tabs from "../Tabs.jsx";
import Tags from "../Tags.js";
import { getResponseText } from "./utils.js";
import Editor from "react-simple-code-editor";
import EllipsesHorizontalIcon from "../../assets/icons/EllipsesHorizontalIcon.jsx";
import SolidSpinnerIcon from "../../assets/icons/SolidSpinnerIcon.jsx";

import PopUp from "../PopUp.js";
import PendingChat from "./PendingChat.jsx";
import PinMessage from "../Pin";
import SqlErrorDialog from "./SqlErrorDialog";
import { IoCloseCircle } from "react-icons/io5";

const TABS = {
  sql_cmd: "SQL Command",
  table: "Table",
  interpreted_question: "Summary",
};

const getNormalizeTabs = (data) => {
  const normalizeTabs = Object.keys(data)
    .map((d) => {
      if (d === "table") {
        const tableData = data[d];

        const chart =
          tableData[Object.keys(data[d])[0]]?.length > 1
            ? { tabName: "Chart", order: 2 }
            : {};
        return [{ tabName: TABS[d], order: 1 }, chart].filter((f) =>
          Boolean(f.tabName)
        );
      }
      return null;
    })
    .filter((d) => d);

  return normalizeTabs.flat(1).sort((a, b) => a.order - b.order);
};

const getTableTitle = (tableName) => {
  const table = tableOptions.find((option) => option.id === tableName);
  return table.name;
};

const Chat = ({ question, reply, id, userQueryAndReport, setDashboards }) => {
  const {
    table_metadata,
    _id: msgId,
    user_reaction,
    selected_tables: selectedTables,
    response_status: status,
    correct_sql,
  } = reply;

  const [reaction, setReaction] = useState(null);
  const [activeTableMetadata, setActiveTableMetadata] = useState(0);
  const [data, setData] = useState(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [activeTab, setActiveTab] = useState();
  const tabs = useMemo(() => (data ? getNormalizeTabs(data) : []), [data]);
  const [activeOption, setActiveOption] = useState();

  const dropdownOptions = useMemo(() => {
    return data
      ? Object.keys(data)
          .map((d) => {
            if (d === "sql_cmd") {
              const isSQLCommandEmpty = data[d];

              return isSQLCommandEmpty
                ? {
                    title: TABS[d],
                    onClick: ({ title }) => {
                      setActiveOption(title);
                      setActiveTab(null);
                    },
                    className: "w-max",
                  }
                : null;
            }
            if (d === "interpreted_question") {
              return {
                title: TABS[d],
                onClick: ({ title }) => {
                  setActiveOption(title);
                  setActiveTab(null);
                },
              };
            }

            return null;
          })
          .filter((item) => Boolean(item))
      : [];
  }, [data]);

  const [code, setCode] = useState(data?.sql_cmd);
  const editorRef = useRef(null);
  const [isSqlUpdateLoading, isSetSqlUpdateLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isSqlDialogOpen, setIsSqlDialogOpen] = useState(false);

  useEffect(() => {
    if (data?.sql_cmd) {
      setCode(data?.sql_cmd);
    }
  }, [data?.sql_cmd]);

  useEffect(() => {
    if (tabs.length) {
      setActiveTab(tabs[0]);
    }
  }, [data]);

  const reactionHandler = async (res) => {
    if (user_reaction === null && reaction === null) {
      setIsFeedbackOpen(true);
      setReaction(res);
      await setMessageReaction(msgId, res);
    }
  };

  useEffect(() => {
    if (table_metadata.length) {
      const tableMetaData = table_metadata[activeTableMetadata];
      setData(tableMetaData);
    }
  }, [activeTableMetadata, table_metadata]);

  useEffect(() => {
    setReaction(user_reaction);
  }, [user_reaction]);

  return (
    <div className="pr-3 mt-3">
      <div className="mx-auto gap-3 md:px-5 lg:px-1 xl:px-5 md:max-w-3xl">
        <div className="pb-2 ">
          {/* <UserIcon className="rounded-full h-8 w-8" /> */}
          <div className="flex flex-col">
            <div className="flex justify-end w-full">
              <p className="text-sm max-w-[90%] text-zinc-100 bg-indigo-600 rounded-xl p-3 ">
                {question}
              </p>
            </div>
            {selectedTables?.length > 0 &&
              selectedTables?.length < tableOptions.length && (
                <div className="px-2 pt-2 flex w-full">
                  <p className="text-xs text-zinc-400 w-22 pr-2">
                    Queried On :
                  </p>
                  <div className="flex gap-3 flex-wrap ">
                    {selectedTables?.map((tableName) => {
                      const title = getTableTitle(tableName);
                      return <Tags tagTitle={title} />;
                    })}
                  </div>
                </div>
              )}
          </div>
        </div>
        <div className="flex h-auto pt-5">
          <HectorIcon className="h-7 w-7" />
          <div className="flex flex-col gap-3 pt-0 h-full w-full pl-2 overflow-x-auto">
            <div className="flex justify-between">
              <PendingChat
                reply={reply}
                tabsLength={tabs.length}
                status={status}
                tableMetadataLength={table_metadata.length}
                userQueryAndReport={userQueryAndReport}
              />
              {table_metadata.length ? <PinMessage setDashboards={setDashboards} msgId={msgId} /> : null}
            </div>
            {table_metadata?.length > 0 && (
              <div className="flex flex-col bg-zinc-800 rounded-xl overflow-hidden p-4">
                <div className="flex bg-black">
                  {table_metadata?.length > 1 ? (
                    table_metadata?.map((data, index) => {
                      return (
                        <div
                          className={`${
                            index === activeTableMetadata
                              ? "text-black bg-white "
                              : "text-zinc-400"
                          } p-2 cursor-pointer`}
                          onClick={() => {
                            setActiveTableMetadata(index);
                          }}
                        >
                          {convertToTitleCase(data?.tool_name)}
                        </div>
                      );
                    })
                  ) : (
                    <div
                      className={`text-zinc-200 bg-zinc-800 w-full flex justify-between`}
                    >
                      <div>{convertToTitleCase(data?.tool_name)}</div>
                      <PopUp
                        activeOption={activeOption}
                        options={[
                          ...dropdownOptions,
                          correct_sql
                            ? {
                                title: "Edited SQL",
                                onClick: () => {
                                  setActiveOption("Edited SQL");
                                  setActiveTab(null);
                                },
                              }
                            : {},
                        ].filter((p) => p.title)}
                      >
                        <EllipsesHorizontalIcon className="text-white w-4 h-4" />
                      </PopUp>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Tabs
                    setActiveOption={setActiveOption}
                    tabs={tabs}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                  />
                  <div>
                    {table_metadata[0]?.summary?.errorsInSQL?.length ? (
                      <SqlErrorDialog
                        isOpen={isSqlDialogOpen}
                        setIsOpen={setIsSqlDialogOpen}
                        errors={table_metadata[0]?.summary?.errorsInSQL}
                      >
                        <div className="text-xs flex gap-1 items-center">
                          <IoCloseCircle className="text-red-500 w-3.5 h-3.5" />
                          <span>SQL Errors</span>
                        </div>
                      </SqlErrorDialog>
                    ) : null}
                  </div>
                </div>

                {activeTab?.tabName === "Table" ? (
                  Object.values(data.table)[0]?.length > 0 ? (
                    <div className="w-full flex flex-col gap-5">
                      <ChatTable
                        table_type={table_metadata?.[0]?.table_type}
                        data={data.table}
                        msgId={msgId}
                      />
                    </div>
                  ) : (
                    <div>
                      It seems there's no data available for the current query.
                      If you have another question or need assistance with a
                      different topic, please feel free to ask, and I'll do my
                      best to help you.
                    </div>
                  )
                ) : null}

                {activeOption === "Summary" && (
                  <div className="whitespace-pre-wrap mt-2 text-sm s">
                    <ul className="space-y-3 ml-4">
                      {table_metadata[0]?.summary?.stepsFollowed?.length
                        ? table_metadata[0]?.summary?.stepsFollowed?.map(
                            (step) => <li className="list-disc">{step}</li>
                          )
                        : getResponseText(status, reply.response_text)}
                    </ul>
                  </div>
                )}

                {activeTab?.tabName === "Chart" ? (
                  <div className="graph">
                    <BarGraph data={data.table} />
                  </div>
                ) : null}

                {activeOption === "Edited SQL" && correct_sql ? (
                  <div
                    className={`editor bg-neutral-900 w-full rounded-lg flex flex-row-reverse focus:ring focus:ring-indigo-500`}
                  >
                    <Editor
                      value={correct_sql}
                      onValueChange={() => {}}
                      highlight={(correct_sql) =>
                        highlight(correct_sql, languages.sql)
                      }
                      padding={10}
                      readOnly
                      textareaClassName="window"
                      className="window"
                      style={{
                        fontFamily: '"Fira code", "Fira Mono", monospace',
                        fontSize: 14,
                      }}
                    />
                  </div>
                ) : null}

                {activeOption === "SQL Command" && (
                  <button
                    onBlur={() => setIsEdit(false)}
                    onClick={() => setIsEdit(true)}
                    className={`editor bg-neutral-900 w-full rounded-lg flex flex-row-reverse focus:ring focus:ring-indigo-500 ${
                      isEdit ? "ring-2 ring-indigo-500" : ""
                    }`}
                  >
                    <div className="self-start mr-2 mt-2">
                      <button
                        onClick={() => {
                          if (code === data?.sql_cmd) return;
                          updateSql({
                            messageId: msgId,
                            code,
                            setLoading: isSetSqlUpdateLoading,
                          });
                        }}
                        className={`flex text-sm gap-1 items-center py-1.5 px-3 border border-zinc-600 rounded-md ${
                          code === data?.sql_cmd
                            ? "opacity-50"
                            : "hover:bg-zinc-700 duration-150"
                        }`}
                      >
                        {isSqlUpdateLoading ? (
                          <div>
                            <SolidSpinnerIcon className="w-4 h-4" />
                          </div>
                        ) : null}
                        <span>Save</span>
                      </button>
                    </div>
                    <Editor
                      ref={editorRef}
                      value={code || ""}
                      onValueChange={(code) => setCode(code)}
                      highlight={(code) => highlight(code, languages.sql)}
                      padding={10}
                      textareaClassName="window"
                      className="window"
                      style={{
                        fontFamily: '"Fira code", "Fira Mono", monospace',
                        fontSize: 14,
                      }}
                    />
                  </button>
                )}
              </div>
            )}

            <div className=" flex gap-3 mt-4">
              {reaction !== 0 && (
                <FiThumbsUp
                  size={20}
                  onClick={() => reactionHandler(1)}
                  className={`${
                    reaction === 1
                      ? "stroke-white stroke-2"
                      : "stroke-1 cursor-pointer"
                  } `}
                />
              )}
              {reaction !== 1 && (
                <FiThumbsDown
                  size={20}
                  onClick={() => reactionHandler(0)}
                  className={`${
                    reaction === 0
                      ? "stroke-white stroke-2"
                      : "stroke-1 cursor-pointer"
                  } `}
                />
              )}
            </div>
            {isFeedbackOpen && (
              <Feedback
                msgId={msgId}
                onCloseClick={() => setIsFeedbackOpen(false)}
                reaction={reaction}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
